import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../../shared/utils/firebase';
import type { LactateTest, ReportType } from '../types';

type CreateLactateTestInput = Omit<
  LactateTest,
  'created_at' | 'updated_at' | 'pdf_url' | 'report_id' | 'prior_initial_test_id'
>;

export interface GenerateReportResult {
  url: string;
  download_url: string;
  report_type: ReportType;
  prior_initial_found: boolean;
}

/**
 * Creates a new lactate_tests document under the client and returns its id.
 * Server timestamps are applied to created_at / updated_at.
 */
export const createLactateTest = async (
  input: CreateLactateTestInput
): Promise<{ testId: string }> => {
  const testDocRef = doc(collection(db, 'clients', input.client_id, 'lactate_tests'));
  await setDoc(testDocRef, {
    ...input,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return { testId: testDocRef.id };
};

/**
 * Uploads the chart image to lactate-tests/{clientId}/{testId}/chart.<ext>,
 * patches the test doc's chart_image_url, and returns the download URL.
 */
export const uploadChartImage = async (
  clientId: string,
  testId: string,
  file: File
): Promise<string> => {
  const parts = file.name.split('.');
  const ext = (parts.length > 1 ? parts[parts.length - 1] : 'png').toLowerCase();
  const storageRef = ref(storage, `lactate-tests/${clientId}/${testId}/chart.${ext}`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  await updateDoc(doc(db, 'clients', clientId, 'lactate_tests', testId), {
    chart_image_url: url,
    updated_at: serverTimestamp(),
  });
  return url;
};

/**
 * Invokes the generateLactateReport callable for the given test and returns
 * the resulting view/download URLs plus report_type and prior_initial_found.
 */
export const requestReportGeneration = async (
  clientId: string,
  testId: string
): Promise<GenerateReportResult> => {
  const callable = httpsCallable<{ clientId: string; testId: string }, GenerateReportResult>(
    functions,
    'generateLactateReport'
  );
  const result = await callable({ clientId, testId });
  return result.data;
};

/**
 * Reads a single lactate test document, or null if it does not exist.
 */
export const getLactateTest = async (
  clientId: string,
  testId: string
): Promise<LactateTest | null> => {
  const snap = await getDoc(doc(db, 'clients', clientId, 'lactate_tests', testId));
  if (!snap.exists()) {
    return null;
  }
  return snap.data() as LactateTest;
};
